import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentType } from 'src/app/shared/enums/document-type.enum';
import { Document } from 'src/app/shared/models/document.model';
import { Education } from 'src/app/shared/models/education.model';
import { Practitioner } from 'src/app/shared/models/practitioner.model';
import { LookupService } from 'src/app/shared/services/lookup.service';

@Component({
  selector: 'app-practitioner-edit-experience',
  templateUrl: './practitioner-edit-experience.component.html',
  styleUrls: ['./practitioner-edit-experience.component.scss']
})
export class PractitionerEditExperienceComponent implements OnInit {
  formGroup: FormGroup;
  languages = ['Select...', 'English', 'German', 'Arabic', 'Spanish', 'French', 'Chinese', 'Portuguese', 'Russian', 'Turkish', 'Hindi', 'Vietnamese'];
  professionalBodies: string[] = [];
  accreditedRegisters: string[] = [];

  @Input() isLoading: boolean;
  @Input() practitioner: Practitioner;
  @Output() dismissed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<FormGroup>();

  constructor(private lookupService: LookupService, private cd: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      'education': this.loadFormArrays(this.practitioner.education.map(edu => edu.name)),
      'degree': this.loadFormArrays(this.practitioner.education.map(edu => edu.degree)),
      'licenses': this.loadFormArrays(this.practitioner.licenses),
      'languages': this.loadFormArrays(this.practitioner.languages),
      'professionalBodies': this.loadFormArrays(this.practitioner.documents.filter(doc => doc.type === DocumentType.ProfessionalBody).map(doc => doc.name)),
      'accreditedRegisters': this.loadFormArrays(this.practitioner.documents.filter(doc => doc.type === DocumentType.AccreditedRegister).map(doc => doc.name))
    });
    this._loadDropdowns();
  }

  loadFormArrays(values: string[]): FormArray {
    let formArray: FormArray = new FormArray([]);
    values.forEach((value, index) => {
      index === 0 ? formArray.push(new FormControl(value, Validators.required)) : formArray.push(new FormControl(value))
    });
    return formArray;
  }

  onSubmit(): void {
    if (!this.formGroup.valid) { return; }
    let finalFormGroup = new FormGroup({
      'education': new FormControl(this._mapEducations()),
      'licenses': new FormControl(this.formGroup.get('licenses').value),
      'languages': new FormControl(this.formGroup.get('languages').value),
      'documents': new FormControl(this._mapDocuments())
    });

    if (this.formGroup.get('education').dirty || this.formGroup.get('degree').dirty) {
      finalFormGroup.get('education').markAsDirty();
    }
    if (this.formGroup.get('licenses').dirty) {
      finalFormGroup.get('licenses').markAsDirty();
    }
    if (this.formGroup.get('languages').dirty) {
      finalFormGroup.get('languages').markAsDirty();
    }
    if (this.formGroup.get('professionalBodies').dirty || this.formGroup.get('accreditedRegisters').dirty) {
      finalFormGroup.get('documents').markAsDirty();
    }

    this.submitted.emit(finalFormGroup);
  }

  _mapDocuments(): Document[] {
    var documents: Document[] = [];
    var value = this.formGroup.value;
    value.professionalBodies.forEach((document: string) => {
      if (document && document.toLowerCase().indexOf('select...') < 0) {
        documents.push(new Document(document, DocumentType.ProfessionalBody, ''));
      }
    });
    value.accreditedRegisters.forEach((document: string) => {
      if (document && document.toLowerCase().indexOf('select...') < 0) {
        documents.push(new Document(document, DocumentType.AccreditedRegister, ''));
      }
    });
    return documents;
  }

  _mapEducations(): Education[] {
    var educations: Education[] = [];
    var value = this.formGroup.value;
    value.education.forEach((education: string, index: number) => {
      educations.push(new Education(education, value.degree[index]));
    });

    return educations;
  }

  _loadDropdowns() {
    this.lookupService.searchProfessionalBodies().then(values => {
      this.professionalBodies = values;
      this.professionalBodies.unshift('Select...');
      this.professionalBodies.push('Other...');
      this.practitioner.documents.filter(doc => doc.type === DocumentType.ProfessionalBody).map(doc => doc.name).forEach((doc, index) => {
        let elem: HTMLSelectElement = document.getElementById('professionalBodySelect' + index) as HTMLSelectElement;
        this.cd.detectChanges();
        elem.value = doc;
      });
    });

    this.lookupService.searchAccreditedRegisters().then(values => {
      this.accreditedRegisters = values;
      this.accreditedRegisters.unshift('Select...');
      this.accreditedRegisters.push('Other...');
      this.practitioner.documents.filter(doc => doc.type === DocumentType.AccreditedRegister).map(doc => doc.name).forEach((doc, index) => {
        let elem: HTMLSelectElement = document.getElementById('accreditedRegisterSelect' + index) as HTMLSelectElement;
        this.cd.detectChanges();
        elem.value = doc;
      });
    })
  }

  _filterLanguages(index: number): string[] {
    var selectedLanguages: string[] = this.formGroup.value.languages;
    if (selectedLanguages.length === 0) {
      return this.languages;
    }

    var filteredLanguages = this.languages.filter(l => {
      return !selectedLanguages.includes(l) || l === selectedLanguages[index];
    });

    return filteredLanguages;
  }

  updateProfessionalBody(index: number, value: string) {
    let isOtherProfessionalBody = value.toLowerCase() === 'other...';
    let newValue = isOtherProfessionalBody ? '' : value;
    let currentProfessionalBodies: string[] = [...this.formGroup.value.professionalBodies];
    currentProfessionalBodies[index] = newValue;
    this.formGroup.patchValue({
      professionalBodies: currentProfessionalBodies
    });
    document.getElementById('professionalBody' + index).hidden = !isOtherProfessionalBody;
    this.cd.detectChanges();
  }

  updateAccreditedRegister(index: number, value: string) {
    let isOtherAccreditedRegister = value.toLowerCase() === 'other...';
    let newValue = isOtherAccreditedRegister ? '' : value;
    let currentAccreditedRegisters: string[] = [...this.formGroup.value.accreditedRegisters];
    currentAccreditedRegisters[index] = newValue;
    this.formGroup.patchValue({
      accreditedRegisters: currentAccreditedRegisters
    });
    document.getElementById('accreditedRegister' + index).hidden = !isOtherAccreditedRegister;
  }

  getControls(type: string): AbstractControl[] {
    return (<FormArray>this.formGroup.get(type)).controls;
  }

  onRemoveControl(type: string, index: number) {
    (<FormArray>this.formGroup.get(type)).removeAt(index);
    this.formGroup.get(type).markAsDirty();
  }

  onAddControl(type: string) {
    (<FormArray>this.formGroup.get(type)).push(new FormControl(null));
    this.formGroup.get(type).markAsDirty();
  }

  containsSelect(control: FormControl): {[s:string]: boolean} {
    if (control.value && control.value.toLowerCase().indexOf('select') > -1) {
      return {'containsSelect': true};
    }
    return null;
  }

  onDismiss(): void {
    this.dismissed.emit();
  }
}
